import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from 'api/apiClient';
import {
  CityGeo,
  GeocodeResponse,
  PolygonsData,
  PolygonsPayload,
} from 'types/maps';

const POLYGONS_QUERY_KEY = 'polygons';
const MAP_CENTER_QUERY_KEY = 'mapCenter';

const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;
const REACT_APP_GEOCODING_API_KEY_DEV =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY_DEV;

/**
 * Get all polygons by address
 */

export const useGetPolygonsByAddress = ({
  companyId,
  targetCompanyId,
  addressId,
  locationId,
}: {
  companyId: string;
  targetCompanyId: string;
  addressId: string;
  locationId: string;
}) =>
  useQuery({
    queryKey: [POLYGONS_QUERY_KEY, locationId, addressId],
    queryFn: () =>
      fetchFromApi<PolygonsData>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-companies/${targetCompanyId}/addresses/${addressId}/locations/${locationId}/polygons`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

/**
 * Create a polygon
 */
export const useCreatePolygon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      targetCompanyId,
      addressId,
      locationId,
    }: {
      payload: PolygonsPayload;
      companyId: string | number;
      targetCompanyId: string;
      addressId: string;
      locationId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-companies/${targetCompanyId}/addresses/${addressId}/locations/${locationId}/polygons`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POLYGONS_QUERY_KEY] });
    },
  });
};

/**
 * Delete a polygon
 */
export const useDeletePolygon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      targetCompanyId,
      addressId,
      locationId,
    }: {
      companyId: string | number;
      targetCompanyId: string;
      addressId: string;
      locationId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-companies/${targetCompanyId}/addresses/${addressId}/locations/${locationId}/polygons`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POLYGONS_QUERY_KEY] });
    },
  });
};

/**
 * Get center of maps by Geocoding API
 */
export const useGetCityCenter = (
  city: string,
  opts?: { countryCode?: string; language?: string; regionBias?: string }
) =>
  useQuery({
    queryKey: [
      MAP_CENTER_QUERY_KEY,
      city?.trim().toLowerCase(),
      opts?.countryCode,
      opts?.language,
    ],
    enabled: Boolean(city?.trim()),
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CityGeo> => {
      const apiKey = REACT_APP_GEOCODING_API_KEY_DEV || '';
      if (!apiKey) {
        throw new Error(
          'Geocoding API key is not configured. Please set REACT_APP_GEOCODING_API_KEY_DEV in your environment.'
        );
      }
      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.set('address', city.trim());
      url.searchParams.set('language', opts?.language ?? 'es');
      if (opts?.countryCode)
        url.searchParams.set('components', `country:${opts.countryCode}`);
      if (opts?.regionBias) url.searchParams.set('region', opts.regionBias);
      url.searchParams.set('key', apiKey);

      const r = await fetch(url.toString());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data: GeocodeResponse = await r.json();

      if (data.status !== 'OK' || !data.results?.length) {
        throw new Error(
          data.error_message || data.status || 'No results found'
        );
      }

      const result =
        data.results.find(
          x => x.types?.includes('locality') || x.types?.includes('postal_town')
        ) ||
        data.results.find(
          x =>
            x.types?.includes('administrative_area_level_2') ||
            x.types?.includes('administrative_area_level_1')
        ) ||
        data.results[0];

      const { geometry, formatted_address, place_id, types } = result;
      return {
        name: formatted_address,
        lat: geometry.location.lat,
        lng: geometry.location.lng,
        bounds: geometry.bounds ?? geometry.viewport,
        placeId: place_id,
        rawTypes: types ?? [],
      };
    },
  });
